import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, Observable } from 'rxjs';
import { UserInfo } from 'src/app/models/user-info.model';
import { UserRightsHelper } from 'src/app/models/role.model';
import { environment } from 'src/environments/environment';
import { DefaultResponse } from 'src/app/models/services/default-reponse.model';
import { EntPhoto } from 'src/app/models/services/users/entPhoto.model';
import { IsmPhoto } from 'src/app/models/ism-photo.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private http = inject(HttpClient);

  private imageUrlSubject = new BehaviorSubject<string>(null);
  imageUrl$ = this.imageUrlSubject.asObservable();

  public setImageUrl(url: string) {
    this.imageUrlSubject.next(url);
  }

  public getImageUrl(): string {
    return this.imageUrlSubject.getValue();
  }

  public isValidImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Check if the image resolution is valid
  public isValidImageResolution(
    base64String: string,
    maxWidth: number,
    maxHeight: number
  ): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const isValid = img.width <= maxWidth && img.height <= maxHeight;
        resolve(isValid);
      };
      img.src = base64String as string;
    });
  }

  // Convert blob to base64
  public blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read the Blob as base64'));
      };
      reader.readAsDataURL(blob);
    });
  }

  // Convert file to base64
  public convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = error => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  // Convert base64 string to Blob
  public convertBase64ToImage(base64: string) {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });

    const imageUrl = URL.createObjectURL(blob);

    return imageUrl;
  }

  // Check if user is S2G - self
  public checkPermissionAddImageSelf(userInfo: UserInfo): boolean {
    const roleChecker = new UserRightsHelper(userInfo.roles, userInfo.orgs);
    return roleChecker.canManagePicturesSelf();
  }

  // Check if user is S2G - general
  public checkPermissionAddImage(userInfo: UserInfo): boolean {
    const roleChecker = new UserRightsHelper(userInfo.roles, userInfo.orgs);
    return roleChecker.canManagePictureS2G();
  }

  public checkIsS2G(userInfo: UserInfo): boolean {
    const roleChecker = new UserRightsHelper(userInfo.roles, userInfo.orgs);
    return roleChecker.canManagePictureS2G();
  }

  /**
   * Add user profile picture
   * @param ismPhoto
   * @returns
   */
  public addProfilePicture(ismPhoto: IsmPhoto): Observable<DefaultResponse> {
    return this.http.post<DefaultResponse>(
      environment.apiUrl + '/Pictures',
      ismPhoto,
      httpOptions
    );
  }

  /**
   * Get user profile picture info by id
   * @param userid
   * @returns
   */
  public getProfilePicture(userid: string): Observable<string | null> {
    return this.http
      .get<string>(environment.apiUrl + '/Pictures/' + userid, httpOptions)
      .pipe(catchError(() => null as string));
  }

  /**
   * Update user profile picture
   * @param ismPhoto
   * @returns
   */
  public updateProfilePicture(ismPhoto: IsmPhoto): Observable<DefaultResponse> {
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + '/Pictures',
      ismPhoto,
      httpOptions
    );
  }

  /**
   * Delete user profile picture info by id
   * @param userid
   * @returns
   */
  public deleteProfilePicture(userid: string): Observable<DefaultResponse> {
    return this.http.delete<DefaultResponse>(
      environment.apiUrl + '/Pictures/' + userid,
      httpOptions
    );
  }

  /**
   * Export images based on send oids
   * @param oids
   * @returns
   */
  public exportProfilePicture(oids: string[]): Observable<EntPhoto[]> {
    return this.http.post<EntPhoto[]>(
      environment.apiUrl + '/Pictures/exportImages',
      oids,
      httpOptions
    );
  }
}
